import { useState, type ChangeEvent } from 'react';
import {
  Alert, Chip, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useGrafik, useGrafikRestauracje } from '@/lib/useAnalytics';
import type { GrafikSzkola } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ADMINISTRACJA → GRAFIK. Kto dowozi, gdzie, w jakie dni i do której godziny można zamawiać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Ekran ma zawsze KONTEKST JEDNEJ RESTAURACJI — tak jak stary panel, w którym grafik otwiera się
 * po wybraniu dostawcy. Bez tego kontekstu tabela „wszyscy do wszystkich" byłaby nieczytelna
 * i nie odpowiadałaby na pytanie, które się tu zadaje.
 *
 * ┌─ DLACZEGO GODZINA BYWA POKAZANA PRZY KAŻDYM DNIU Z OSOBNA ──────────────────────────────────┐
 * │ Godzina zamknięcia zamówień jest w replice zapisana PER DZIEŃ, nie przy parze szkoła–       │
 * │ restauracja. Zwykle wszystkie dni mają tę samą — i wtedy pokazujemy ją raz. Ale gdy się     │
 * │ różnią, rozbijamy je na dni, bo zwinięcie ukryłoby ten jeden dzień, w którym zamówienia     │
 * │ zamykają się wcześniej. To jest dokładnie ten dzień, przez który ktoś nie zdąży zamówić.    │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * BRAK GODZINY NIE JEST PÓŁNOCĄ. Pusta wartość znaczy „nie wiemy" i tak jest podpisana.
 * Podstawienie 00:00 zamykałoby zamówienia natychmiast, 23:59 — otwierało je na dobę.
 * Obie liczby wyglądają na dane i obie byłyby zmyślone.
 */

/** Skrót dnia do nagłówka kolumny. Pełna nazwa zostaje w tytule najazdu. */
const SKROT = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];

function KomorkaDnia({ szkola, numer }: { szkola: GrafikSzkola; numer: number }) {
  const dzien = szkola.dni.find((d) => d.numer === numer);

  if (!dzien || !dzien.aktywny) {
    return <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
  }
  if (!dzien.cutoff_znany) {
    return (
      <Tooltip title="Dostawa tego dnia jest, ale godziny zamknięcia zamówień nie znamy. Nie zakładamy północy.">
        <Chip size="small" label="bez godziny" color="warning" variant="outlined" />
      </Tooltip>
    );
  }
  return <Chip size="small" label={dzien.cutoff!.tekst} />;
}

export function GrafikPage() {
  const restauracje = useGrafikRestauracje();

  /*
   * Kontekst startowy to PIERWSZA restauracja z listy — ekran bez wyboru nie ma o czym mówić.
   *
   * Wyprowadzamy go z danych, zamiast dosyłać efektem po ich przyjściu. Efekt ustawiający stan
   * zaraz po renderze wymusza drugi render na każdym wejściu na ekran, a przy okazji tworzy
   * moment, w którym lista już jest, a wybór jeszcze nie — czyli migot pustego ekranu.
   */
  const [wybranaRecznie, setWybranaRecznie] = useState<number | null>(null);
  const wybrana = wybranaRecznie ?? restauracje.data?.restauracje?.[0]?.id ?? null;
  const setWybrana = setWybranaRecznie;

  const grafik = useGrafik(wybrana);
  const szkoly = grafik.data?.szkoly ?? [];

  const niejednolite = szkoly.filter((s) => !s.cutoff_jednolity);
  const bezGodziny = szkoly.filter((s) => s.dni_bez_cutoffu.length > 0);

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Grafik"
        opis="Która restauracja dowozi do której szkoły, w jakie dni i do której godziny można zamawiać."
      />

      <Karta>
        <Stack direction="row" spacing={2} useFlexGap sx={{ p: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Restauracja"
            select
            size="small"
            sx={{ minWidth: 320 }}
            value={wybrana ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setWybrana(Number(e.target.value))}
          >
            {(restauracje.data?.restauracje ?? []).map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.nazwa} — {r.szkol} {r.szkol === 1 ? 'szkoła' : 'szkół'}
              </MenuItem>
            ))}
          </TextField>

          {grafik.data && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {grafik.data.zrodlo.pochodzenie}. {grafik.data.zrodlo.konwencja_dni}.
            </Typography>
          )}
        </Stack>
      </Karta>

      {niejednolite.length > 0 && (
        <Alert severity="warning">
          <strong>Godziny zamknięcia zamówień NIE są jednakowe we wszystkie dni</strong> dla{' '}
          {niejednolite.length} {niejednolite.length === 1 ? 'szkoły' : 'szkół'}. Każdy dzień ma
          poniżej własną godzinę — pokazanie jednej wspólnej ukryłoby dzień, w którym zamówienia
          zamykają się wcześniej.
        </Alert>
      )}

      {bezGodziny.length > 0 && (
        <Alert severity="info">
          W {bezGodziny.length} {bezGodziny.length === 1 ? 'szkole' : 'szkołach'} są dni dostaw{' '}
          <strong>bez zapisanej godziny zamknięcia zamówień</strong>. Nie przyjmujemy za nie
          północy — takie dni są oznaczone jako „bez godziny".
        </Alert>
      )}

      {(restauracje.isLoading || grafik.isLoading) && <LoadingState />}
      {restauracje.error ? <ErrorState error={restauracje.error} context="Lista restauracji z grafikiem." /> : null}
      {grafik.error ? <ErrorState error={grafik.error} context="Grafik wybranej restauracji." /> : null}

      {!grafik.isLoading && !grafik.error && wybrana !== null && szkoly.length === 0 && (
        <EmptyState
          title="Ta restauracja nie ma szkół w grafiku"
          description="Grafik pochodzi z repliki systemu głównego. Brak wpisów znaczy, że ta restauracja nie ma przypisanych dostaw — albo że replikacja ich jeszcze nie przeniosła."
        />
      )}

      {szkoly.length > 0 && (
        <Karta>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Grafik dostaw — szkoły i dni tygodnia">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                  {SKROT.map((s, i) => (
                    <TableCell key={s} align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      <Tooltip title={grafik.data?.dni_tygodnia[i]?.nazwa ?? ''}>
                        <span>{s}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Zamówienia do</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {szkoly.map((sz) => (
                  <TableRow key={sz.szkola_id} hover>
                    <TableCell>
                      {sz.szkola}
                      {!sz.szkola_nazwa_znana && (
                        <Tooltip title="Szkoła ma dostawy, ale jej nazwy nie ma w słowniku CRM. Ukrycie takiego wiersza zataiłoby realne dostawy.">
                          <Chip size="small" label="?" sx={{ ml: 0.5 }} variant="outlined" />
                        </Tooltip>
                      )}
                    </TableCell>
                    {SKROT.map((_, i) => (
                      <TableCell key={i} align="center">
                        <KomorkaDnia szkola={sz} numer={i} />
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {sz.cutoff_jednolity
                        ? (sz.cutoff_wspolny ?? <Typography component="span" variant="body2"
                            sx={{ color: 'text.disabled' }}>nieznana</Typography>)
                        : <Chip size="small" color="warning" variant="outlined" label="różna w różne dni" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack sx={{ p: 2 }}>
            {/*
              Grafik jest tu do CZYTANIA. Zmiana przypisań i godzin to operacja zapisu w systemie
              głównym, a Control Center pracuje w trybie tylko do odczytu — mówimy to wprost,
              zamiast pokazywać sterowanie, które i tak by nie zadziałało.
            */}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Widok tylko do odczytu. Przypisania szkół, dni dostaw i godziny zamknięcia zamówień
              zmienia się w systemie głównym — Control Center pokazuje ich replikę.
            </Typography>
          </Stack>
        </Karta>
      )}
    </Stack>
  );
}

export default GrafikPage;
